<script lang="ts">
import { defineComponent } from "vue";
import * as yup from "yup";

import { useAuthStore, type Credential, type Type } from "./stores/useAuthStore";

import FormInputDefaultComponent from "@/components/Forms/Input/FormInputDefaultComponent.vue";
import ButtonDefaultComponent from "@/components/Forms/Button/ButtonDefaultComponent.vue";
import FormCheckBoxDefaultComponent from "@/components/Forms/CheckBox/FormCheckBoxDefaultComponent.vue";

import { useMasterDataStore } from "@/stores/Shared/useMasterDataStore";
import { useRegisterInformationStore } from "./stores/useRegisterInformationStore"
import FormSelectBoxDefaultComponent from "@/components/Forms/SelectBox/FormSelectBoxDefaultComponent.vue";
import { ENUM_FIELD_LANGUAGE } from "@/configs/enums/enum";
import type { $enum, $string } from "@/configs/types/Shared/typeShare";
import DesktopHeaderComponent from "@/components/Headers/DesktopHeaderComponent.vue";
import MobileHeaderComponent from "@/components/Headers/MobileHeaderComponent.vue";
import { useSharedStore as useSharedPoliceCommandCenterStore } from "@/stores/PoliceCommandCenter/useSharedStore";
import { useSharedStore as useSharedPoliceTouristAndAgentStore } from "@/stores/PoliceTouristAndAgent/useSharedStore";
import { IonNavLink } from "@ionic/vue";
import { appVersion, getLocalStorage, isCommandCenterApp, setLocalStorage } from "@/configs/helpers/MainHelper";
import { useSettingLanguageStore } from "../Tourist/Setting/stores/useSettingLanguageStore";
import { storeName as storeNamePoliceTouristAndAgent } from "@/stores/PoliceTouristAndAgent/useSharedStore";
import { storeName as storeNamePoliceCommandCenter } from "@/stores/PoliceCommandCenter/useSharedStore";
import { Capacitor } from "@capacitor/core";

export default defineComponent({
  name: "LoginView",
  components: {
    FormInputDefaultComponent,
    ButtonDefaultComponent,
    FormCheckBoxDefaultComponent,
    IonNavLink,
  },
  data() {
    return {
      appVersion: import.meta.env.VITE_APP_VERSION,
      componentName: this.$options.name,
      form: {
        email: "",
        password: "",
        rememberMe: false,
      } as Credential,
      type: {
        password: "password",
      } as Type,
      formError: {} as { [key: string]: string },
      errorMessage: "",
      loading: false,
      validate: yup.object().shape({
        email: yup.string().trim().required(this.$t("form.email.placeholder")),
        password: yup.string().min(6, "รหัสผ่านควรมีอย่างน้อย 6 ตัวอักษร").required(this.$t("form.password.placeholder")),
      }),
      isRoute: isCommandCenterApp,
      icons: {
        facebook: `<svg class="ttp-icon-inside-box-03" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Frame" clip-path="url(#clip0_459_6294)"><path id="Vector" d="M12 0.5C5.3724 0.5 0 5.8724 0 12.5C0 19.1276 5.3724 24.5 12 24.5C18.6276 24.5 24 19.1276 24 12.5C24 5.8724 18.6276 0.5 12 0.5Z" fill="white"/><path id="Vector_2" d="M13.7285 17.3578H16.9851L17.4966 14.0496H13.7285V12.2414C13.7285 10.8673 14.1778 9.64861 15.4632 9.64861H17.5287V6.76199C17.1657 6.71292 16.3981 6.60596 14.9479 6.60596C11.9191 6.60596 10.1435 8.2053 10.1435 11.8494V14.0503H7.02979V17.3584H10.1429V26.4512C10.7595 26.543 11.3843 26.606 12.0254 26.606C12.6048 26.606 13.1705 26.5531 13.7285 26.4776V17.3578Z" fill="#1877F2"/></g><defs><clipPath id="clip0_459_6294"><rect width="24" height="24" fill="white" transform="translate(0 0.5)"/></clipPath></defs></svg>`,
        google: `<svg class="ttp-icon-inside-box-03" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"> <g id="Frame" clip-path="url(#clip0_459_5101)"> <path id="Vector" d="M23.5078 13.1668C23.5078 12.1836 23.4295 11.466 23.2598 10.722H12.2295V15.1599H18.7041C18.5736 16.2628 17.8687 17.9238 16.3022 19.0399L16.2802 19.1885L19.7678 21.9387L20.0095 21.9632C22.2285 19.877 23.5078 16.8076 23.5078 13.1668Z" fill="#4285F4"/> <path id="Vector_2" d="M12.2296 24.8599C15.4016 24.8599 18.0645 23.7968 20.0095 21.9632L16.3023 19.0399C15.3102 19.7441 13.9787 20.2358 12.2296 20.2358C9.1228 20.2358 6.48598 18.1497 5.54603 15.2663L5.40825 15.2782L1.7818 18.135L1.73438 18.2692C3.6663 22.1757 7.63462 24.8599 12.2296 24.8599Z" fill="#34A853"/> <path id="Vector_3" d="M5.54621 15.2663C5.2982 14.5222 5.15466 13.7249 5.15466 12.9011C5.15466 12.0772 5.2982 11.28 5.53316 10.5359L5.52659 10.3774L1.8547 7.47473L1.73457 7.5329C0.938328 9.154 0.481445 10.9744 0.481445 12.9011C0.481445 14.8278 0.938328 16.6481 1.73457 18.2692L5.54621 15.2663Z" fill="#FBBC05"/> <path id="Vector_4" d="M12.2295 5.56635C14.4356 5.56635 15.9237 6.53634 16.7722 7.34693L20.0878 4.0516C18.0515 2.12492 15.4015 0.942322 12.2295 0.942322C7.63462 0.942322 3.6663 3.62638 1.73438 7.53289L5.53297 10.5359C6.48597 7.65252 9.12279 5.56635 12.2295 5.56635Z" fill="#EB4335"/> </g> <defs> <clipPath id="clip0_459_5101"> <rect width="23.04" height="24" fill="white" transform="translate(0.47998 0.942322)"/> </clipPath> </defs> </svg>`,
        apple: `<svg class="ttp-icon-inside-box-03" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"> <g id="Frame" clip-path="url(#clip0_459_5110)"> <g id="Group"> <path id="Vector" d="M17.2729 0.396624C17.2219 0.339624 15.3844 0.419124 13.7854 2.15462C12.1864 3.88862 12.4324 5.87762 12.4684 5.92862C12.5044 5.97962 14.7484 6.05912 16.1809 4.04162C17.6134 2.02412 17.3239 0.455124 17.2729 0.396624ZM22.2439 17.9961C22.1719 17.8521 18.7564 16.1451 19.0744 12.8631C19.3924 9.57962 21.5869 8.67962 21.6214 8.58212C21.6559 8.48462 20.7259 7.39712 19.7404 6.84662C19.0168 6.4585 18.216 6.23614 17.3959 6.19562C17.2339 6.19112 16.6714 6.05312 15.5149 6.36962C14.7529 6.57812 13.0354 7.25312 12.5629 7.28012C12.0889 7.30712 10.6789 6.49712 9.16239 6.28262C8.19189 6.09512 7.16289 6.47912 6.42639 6.77462C5.69139 7.06862 4.29339 7.90562 3.31539 10.1301C2.33739 12.3531 2.84889 15.8751 3.21489 16.9701C3.58089 18.0636 4.15239 19.8561 5.12439 21.1641C5.98839 22.6401 7.13439 23.6646 7.61289 24.0126C8.09139 24.3606 9.44139 24.5916 10.3774 24.1131C11.1304 23.6511 12.4894 23.3856 13.0264 23.4051C13.5619 23.4246 14.6179 23.6361 15.6994 24.2136C16.5559 24.5091 17.3659 24.3861 18.1774 24.0561C18.9889 23.7246 20.1634 22.4676 21.5344 19.9191C22.0549 18.7341 22.2919 18.0936 22.2439 17.9961Z" fill="white"/> <path id="Vector_2" d="M17.2729 0.396624C17.2219 0.339624 15.3844 0.419124 13.7854 2.15462C12.1864 3.88862 12.4324 5.87762 12.4684 5.92862C12.5044 5.97962 14.7484 6.05912 16.1809 4.04162C17.6134 2.02412 17.3239 0.455124 17.2729 0.396624ZM22.2439 17.9961C22.1719 17.8521 18.7564 16.1451 19.0744 12.8631C19.3924 9.57962 21.5869 8.67962 21.6214 8.58212C21.6559 8.48462 20.7259 7.39712 19.7404 6.84662C19.0168 6.4585 18.216 6.23614 17.3959 6.19562C17.2339 6.19112 16.6714 6.05312 15.5149 6.36962C14.7529 6.57812 13.0354 7.25312 12.5629 7.28012C12.0889 7.30712 10.6789 6.49712 9.16239 6.28262C8.19189 6.09512 7.16289 6.47912 6.42639 6.77462C5.69139 7.06862 4.29339 7.90562 3.31539 10.1301C2.33739 12.3531 2.84889 15.8751 3.21489 16.9701C3.58089 18.0636 4.15239 19.8561 5.12439 21.1641C5.98839 22.6401 7.13439 23.6646 7.61289 24.0126C8.09139 24.3606 9.44139 24.5916 10.3774 24.1131C11.1304 23.6511 12.4894 23.3856 13.0264 23.4051C13.5619 23.4246 14.6179 23.6361 15.6994 24.2136C16.5559 24.5091 17.3659 24.3861 18.1774 24.0561C18.9889 23.7246 20.1634 22.4676 21.5344 19.9191C22.0549 18.7341 22.2919 18.0936 22.2439 17.9961Z" fill="white"/> </g> </g> <defs> <clipPath id="clip0_459_5110"> <rect width="24" height="24" fill="white" transform="translate(0.5 0.384613)"/> </clipPath> </defs> </svg>`,
      },
      isAndroid: false,
      languages: {
        cn: new URL(`@/assets/images/languages/flag-cn.webp`, import.meta.url).href,
        en: new URL(`@/assets/images/languages/flag-en.webp`, import.meta.url).href,
        jp: new URL(`@/assets/images/languages/flag-jp.webp`, import.meta.url).href,
        kr: new URL(`@/assets/images/languages/flag-kr.png`, import.meta.url).href,
        ru: new URL(`@/assets/images/languages/flag-ru.png`, import.meta.url).href,
        th: new URL(`@/assets/images/languages/flag-th.png`, import.meta.url).href,
      },
      isLanguage: "en",
      isNext: false,
    };
  },
  computed: {
    components() {
      return {
        header: {
          desktop_header: {
            component: DesktopHeaderComponent,
          },
          mobile_header: {
            component: MobileHeaderComponent,
          },
        },
        form: {
          input: {
            email: {
              component: FormInputDefaultComponent,
              props: {
                name: "email",
                isSpecial: true,
                isVisible: true,
                isError: this.formError["email"]?.length > 0,
                isIcon: true,
                isDisable: this.loading,
                isRequired: true,
                isStartIcon: true,
                textError: this.formError["email"],
                componentIcon: this.$solidIcons.EnvelopeIcon,
                classIcon: "ttp-icon-inside-box-03 text-ttp-placeholder",
                title: this.$t("form.email.label"),
                placeholder: this.$t("form.email.placeholder"),
                value: this.form.email,
              } as typeof FormInputDefaultComponent.props,
              onUpdateValue: (value: any) => {
                this.form.email = value.trim();
              },
            },
            password: {
              component: FormInputDefaultComponent,
              props: {
                name: "password",
                isSpecial: true,
                isVisible: true,
                isError: this.formError["password"]?.length > 0,
                isIcon: true,
                isDisable: this.loading,
                isRequired: true,
                isStartIcon: true,
                textError: this.formError["password"],
                componentIcon: this.$solidIcons.LockClosedIcon,
                componentIcon_2: this.$solidIcons.EyeSlashIcon,
                componentIcon_3: this.$solidIcons.EyeIcon,
                classIcon: "ttp-icon-inside-box-03 text-ttp-placeholder",
                type: this.type.password,
                title: this.$t("form.password.label"),
                placeholder: this.$t("form.password.placeholder"),
                value: this.form.password,
              } as typeof FormInputDefaultComponent.props,
              onUpdateValue: (value: any) => {
                this.form.password = value.trim();
              },
              onType: () => {
                this.type.password = this.type.password == "password" ? "text" : "password";
              },
            },
          },
          select: {
            lang: {
              component: FormSelectBoxDefaultComponent,
              props: {
                isSpecial: false,
                isVisible: true,
                placeholder: this.$t("page.LoginView.lang"),
                value: this.sharedStore.locale.toUpperCase(),
                clearable: false,
                items: Object.keys(ENUM_FIELD_LANGUAGE).map((key) => ({
                  label: this.$t(`lang.${String(key).toLowerCase()}`),
                  value: ENUM_FIELD_LANGUAGE[key as keyof typeof ENUM_FIELD_LANGUAGE],
                })),
              } as typeof FormSelectBoxDefaultComponent.props,
              onUpdateValue: (value: ENUM_FIELD_LANGUAGE | $enum) => {
                this.setLang(value);
              },
            },
          },
          button: {
            btnSignin: {
              component: ButtonDefaultComponent,
              props: {} as typeof ButtonDefaultComponent.props,
            },
            btnLanguage: {
              component: ButtonDefaultComponent,
              props: {} as typeof ButtonDefaultComponent.props,
            },
          },
          checkbox: {
            rememberMe: {
              component: FormCheckBoxDefaultComponent,
              props: {} as typeof FormCheckBoxDefaultComponent.props,
            },
          },
        },
      };
    },
    hiddenFields() {
      return this.registerInformationStore.hiddenFields
    }
  },
  methods: {
    toggleMuteMicrophone() {
      if (Capacitor.isNativePlatform()) {
        console.log("Toggle microphone on native platform.");
      } else {
        console.log("Toggle microphone on web platform.");
      }
    },
    toggleMuteSpeaker() {
      if (Capacitor.isNativePlatform()) {
        console.log("Toggle speaker on native platform.");
      } else {
        console.log("Toggle speaker on web platform.");
      }
    },
    switchAudioOutput(outputType: any) {
      if (Capacitor.isNativePlatform()) {
        console.log(`Switch audio output to ${outputType} on native platform.`);
      } else {
        console.log(`Switch audio output to ${outputType} on web platform.`);
      }
    },
    switchMicrophoneInput(inputType: any) {
      if (Capacitor.isNativePlatform()) {
        console.log(`Switch microphone input to ${inputType} on native platform.`);
      } else {
        console.log(`Switch microphone input to ${inputType} on web platform.`);
      }
    },
    async submitLanguage() {
      this.isNext = true;
      this.sharedStore.locale = String(this.isLanguage).toUpperCase();
      await setLocalStorage(`${this.storeName}:locale`, String(this.isLanguage).toUpperCase());
      this.$i18n.locale = String(this.isLanguage).toLowerCase();
    },
    async setLang(value: ENUM_FIELD_LANGUAGE | $enum | $string) {
      this.sharedStore.locale = String(value).toUpperCase();
      await setLocalStorage(`${this.storeName}:locale`, String(value).toUpperCase());
      this.$i18n.locale = String(value).toLowerCase();
    },
    async onSubmit(e: Event) {
      if (e) e.preventDefault();

      this.loading = true;
      this.resetError();

      try {
        await this.validate.validate(this.form, {
          abortEarly: false,
        });

        const user = await this.authStore.login(this.form);
        this.settingLanguageStore.result = user;
        this.sharedStore.locale = String(await getLocalStorage(`${this.storeName}:locale`)).toUpperCase();
        await setLocalStorage(`${this.storeName}:locale`, String(this.sharedStore.locale).toUpperCase());
        this.$i18n.locale = String(this.sharedStore.locale).toLowerCase();
        this.settingLanguageStore.result.locale = this.sharedStore.locale;
        this.settingLanguageStore.onSubmit(e);

        await this.fetch();
        this.$router.push({ name: "HomeView" });
      } catch (e) {
        if (e instanceof yup.ValidationError) {
          e.inner.forEach(({ path, message }) => {
            if (path) {
              this.formError[path] = message;
            }
          });
        } else {
          this.errorMessage = this.$t("error.invalid_credential");
        }
      } finally {
        this.loading = false;
      }
    },
    resetError() {
      this.formError = {};
      this.errorMessage = "";
    },
    async fetch() {
      await Promise.all([
        this.masterDataStore.listTitle(),
        this.masterDataStore.listLocale(),
        this.masterDataStore.listRole(),
        this.masterDataStore.listNationality(),
        this.masterDataStore.listIncidentCategory(),
        this.masterDataStore.listProvince(),
        this.masterDataStore.listCountry(),
      ]);
    },
    async facebookLoginHandler() {
      await this.authStore.loginWithThirdParty("FACEBOOK");
      if (this.authStore.user) {
        await this.fetch();
        this.$router.push({ name: "HomeView" });
      } else {
        this.$router.push({ name: "RegisterView" });
      }
    },
    async googleLoginHandler() {
      await this.authStore.loginWithThirdParty("GOOGLE");
      if (this.authStore.user) {
        await this.fetch();
        this.$router.push({ name: "HomeView" });
      } else {
        this.$router.push({ name: "RegisterView" });
      }
    },
    async appleLoginHandler() {
      await this.authStore.loginWithThirdParty("APPLE");
      if (this.authStore.user) {
        await this.fetch();
        this.$router.push({ name: "HomeView" });
      } else {
        this.$router.push({ name: "RegisterView" });
      }
    },
    async phoneLoginHandler() {
      await this.authStore.loginWithThirdParty("PHONE");
      if (this.authStore.user) {
        await this.fetch();
        this.$router.push({ name: "HomeView" });
      } else {
        this.$router.push({ name: "RegisterView" });
      }
    },
    async setting() {
      await this.setLang(this.$enums.ENUM_FIELD_LANGUAGE.EN);
      await setLocalStorage(`${this.storeName}:getProjectType`, String(this.$enums.ENUM_APP_CATEGORY_NAME.TOURIST));
      this.registerInformationStore.getHiddenFields()
    },
    logout() {
      this.authStore.logout()
    },
  },
  async mounted() {
    // App Version
    this.appVersion = await appVersion();
    await this.setting();
  },
  async unmounted() {},
  async created() {
    // Check Android App
    this.isAndroid = window.navigator.userAgent.toLowerCase().includes("android");
  },
  setup() {
    const authStore = useAuthStore();
    const masterDataStore = useMasterDataStore();
    const sharedStore = isCommandCenterApp ? useSharedPoliceCommandCenterStore() : useSharedPoliceTouristAndAgentStore();
    const settingLanguageStore = useSettingLanguageStore();
    const registerInformationStore = useRegisterInformationStore()
    masterDataStore.listNationality();

    return {
      authStore,
      masterDataStore,
      registerInformationStore,
      sharedStore,
      settingLanguageStore,
      storeName: isCommandCenterApp ? storeNamePoliceCommandCenter : storeNamePoliceTouristAndAgent,
    };
  },
});
</script>

<template>
  <component :is="components.header.mobile_header.component" class="md:hidden -top-[26vw] sm:-top-[30vw]" />
  <component :is="components.header.desktop_header.component" class="hidden md:block md:-top-[0vw] lg:-top-[6vw] xl:-top-[10vw]" />
  <div id="page-container" class="mx-auto flex min-h-screen w-full min-w-[320px] flex-col bg-white text-ttp-text-primary">
    <main id="page-content" class="flex max-w-full flex-auto flex-col">
      <div class="mx-auto flex min-h-screen w-full max-w-10xl justify-center p-4">
        <div v-if="isNext" class="w-full max-w-5xl">
          <div class="overflow-hidden">
            <section class="relative flex items-center justify-center">
              <div class="relative text-center sm:text-left">
                <img src="@/assets/images/ttp-logo.png" class="ttp-icon-inside-box-05 opacity-0" />
              </div>
            </section>
            <section class="relative z-0">
              <div class="flex mb-2">
                <h1 class="w-full mb-1 ttp-text-2xl font-bold">
                  {{ $t("page.LoginView.title") }}
                </h1>
                <div class="flex gap-4 items-center justify-center">
                  <component :classTagName="'w-32'" :is="components.form.select.lang.component" v-bind="components.form.select.lang.props" @updateValue="components.form.select.lang.onUpdateValue" />
                  <Menu as="div" class="inline-block relative">
                    <MenuButton class="p-2">
                      <component :is="$outlineIcons.EllipsisVerticalIcon" class="inline-block ttp-icon-inside-box-03" />
                    </MenuButton>
                    <MenuItems class="absolute right-1 origin-top-right z-10 mt-2 w-4/4 shadow-md rounded-lg dark:shadow-gray-900 focus:outline-none z-[100]">
                      <div class="bg-white ring-1 ring-black ring-opacity-5 rounded-lg divide-y divide-gray-100 dark:bg-gray-800 dark:divide-gray-700 dark:ring-gray-700">
                        <div class="space-y-1">
                          <MenuItem v-slot="{ active }">
                            <router-link
                              :to="{ name: 'LoginPoliceCommandCenterView' }"
                              class="group ttp-text-xs font-medium flex items-center justify-between space-x-2 px-2.5 py-2 rounded-lg border border-transparent"
                              :class="{
                                'text-teal-800 bg-teal-50 dark:text-white dark:bg-gray-700/75 dark:border-transparent': active,
                                'text-gray-700 hover:text-teal-800 hover:bg-teal-50 active:border-teal-100 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700/75 dark:active:border-gray-600': !active,
                              }"
                            >
                              <span class="grow text-gray-500 ttp-text-xs font-normal">Police</span>
                            </router-link>
                          </MenuItem>
                        </div>
                        <div class="space-y-1">
                          <MenuItem v-slot="{ active }">
                            <router-link :to="{ name: 'LoginView' }" class="group ttp-text-xs font-medium flex items-center justify-between space-x-2 px-2.5 py-2 rounded-lg border border-transparent" :class="{ 'text-teal-800 bg-teal-50 dark:text-white dark:bg-gray-700/75 dark:border-transparent': active, 'text-gray-700 hover:text-teal-800 hover:bg-teal-50 active:border-teal-100 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700/75 dark:active:border-gray-600': !active }">
                              <span class="grow text-gray-500 ttp-text-xs font-normal">Tourist</span>
                            </router-link>
                          </MenuItem>
                        </div>
                      </div>
                    </MenuItems>
                  </Menu>
                </div>
              </div>

              <form @submit="onSubmit">
                <div class="grid grid-cols-12 gap-2">
                  <component :classTagName="'col-start-1 col-end-13'" :is="components.form.input.email.component" v-bind="components.form.input.email.props" @updateValue="components.form.input.email.onUpdateValue" />
                  <component :classTagName="'col-start-1 col-end-13'" :is="components.form.input.password.component" v-bind="components.form.input.password.props" @updateValue="components.form.input.password.onUpdateValue" @type="components.form.input.password.onType" />
                  <div class="col-start-1 col-end-13">
                    <div class="flex mb-4">
                      <div class="w-full text-left">
                        <RouterLink
                          :to="{ name: 'RegisterView' }"
                          class="ml-2 ttp-text-xs font-medium inline-block text-ttp-primary hover:text-ttp-primary-dark"
                          @click="logout"
                        >
                          {{ $t("btn.btn_register") }}
                        </RouterLink>
                      </div>
                      <div class="w-full text-right">
                        <RouterLink
                          :to="{ name: 'ForgotPasswordView' }"
                          class="ml-2 ttp-text-xs font-normal inline-block text-ttp-primary hover:text-ttp-primary-dark"
                        >
                          {{ $t("btn.btn_forgot_password") }} ?
                        </RouterLink>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="ttp-between">
                  <ButtonDefaultComponent :classTagName="'w-full'" :isSpecial="false" :isVisible="true" :isIcon="true" :isLoading="loading" :isDisable="loading" :htmlIcon="''" :isSubmit="true" :title="$t('btn.btn_sign_in')" :classEnumName="$enums.ENUM_COLOR.DEFAULT_1" :size="$enums.ENUM_SIZE.SMALL" />
                </div>
                <small v-if="errorMessage.length" class="mt-2 text-ttp-text-error">
                  {{ errorMessage }}
                </small>
              </form>
              <h3 class="my-8 flex items-center">
                <span aria-hidden="true" class="h-0.5 grow rounded bg-gray-200 dark:bg-gray-700/75"></span>
                <span class="ttp-text-xs mx-3">{{ $t("divider.or_login_with") }}</span>
                <span aria-hidden="true" class="h-0.5 grow rounded bg-gray-200 dark:bg-gray-700/75"></span>
              </h3>
              <div v-if="hiddenFields.loginWithFacebook" class="ttp-between mb-2">
                <IonNavLink @click="facebookLoginHandler" expand="block" class="w-full">
                  <ButtonDefaultComponent :classTagName="'w-full'" :isSpecial="false" :isVisible="true" :isIcon="true" :isLoading="loading" :isDisable="loading" :htmlIcon="icons.facebook" :classIcon="'ttp-icon-inside-box-03 inline-block'" :isSubmit="false" :title="$t('btn.btn_fb')" :classEnumName="$enums.ENUM_COLOR.DEFAULT_2" :size="$enums.ENUM_SIZE.SMALL" />
                </IonNavLink>
              </div>
              <div class="ttp-between mb-2">
                <IonNavLink @click="googleLoginHandler" expand="block" class="w-full">
                  <ButtonDefaultComponent :classTagName="'w-full'" :isSpecial="false" :isVisible="true" :isIcon="true" :isLoading="loading" :isDisable="loading" :htmlIcon="icons.google" :classIcon="'ttp-icon-inside-box-03 inline-block'" :isSubmit="true" :title="$t('btn.btn_google')" :classEnumName="$enums.ENUM_COLOR.OPACITY" :size="$enums.ENUM_SIZE.SMALL" />
                </IonNavLink>
              </div>
              <div v-if="!isAndroid" class="ttp-between mb-2">
                <IonNavLink @click="appleLoginHandler" expand="block" class="w-full">
                  <ButtonDefaultComponent :classTagName="'w-full'" :isSpecial="false" :isVisible="true" :isIcon="true" :isLoading="loading" :isDisable="loading" :htmlIcon="icons.apple" :classIcon="'ttp-icon-inside-box-03 inline-block'" :isSubmit="true" :title="$t('btn.btn_apple')" :classEnumName="$enums.ENUM_COLOR.BLACK" :size="$enums.ENUM_SIZE.SMALL" />
                </IonNavLink>
              </div>
              <div v-if="hiddenFields.loginWithPhoneNumber" class="ttp-between mb-2">
                <IonNavLink @click="phoneLoginHandler" expand="block" class="w-full">
                  <ButtonDefaultComponent :classTagName="'w-full'" :isSpecial="false" :isVisible="true" :isIcon="true" :isLoading="loading" :isDisable="loading" :classIcon="'ttp-icon-inside-box-03 inline-block'" :isSubmit="false" :title="$t('btn.btn_login_with_phone')" :classEnumName="$enums.ENUM_COLOR.YELLOW" :size="$enums.ENUM_SIZE.SMALL" />
                </IonNavLink>
              </div>
            </section>
          </div>
          <div class="text-right ttp-text-xs text-[#94a3b8]">v.{{ appVersion }}</div>
        </div>
        <div v-else class="w-full max-w-5xl">
          <div class="overflow-hidden">
            <section class="relative flex items-center justify-center">
              <div class="relative text-center sm:text-left">
                <img src="@/assets/images/ttp-logo.png" class="ttp-icon-inside-box-05 opacity-0" />
              </div>
            </section>
            <section class="relative z-0">
              <div class="flex flex-col mb-2">
                <h1 class="w-full mb-1 ttp-text-2xl font-bold">
                  {{ $t("page.LoginView.lang") }}
                </h1>
                <div class="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-10">
                  <button @click="isLanguage = 'en'" :class="{ 'border-ttp-primary border-4': isLanguage == 'en' }" class="md:p-4 rounded-xl overflow-hidden">
                    <img :src="languages.en" />
                  </button>
                  <button @click="isLanguage = 'th'" :class="{ 'border-ttp-primary border-4': isLanguage == 'th' }" class="md:p-4 rounded-xl overflow-hidden">
                    <img :src="languages.th" />
                  </button>
                  <button @click="isLanguage = 'cn'" :class="{ 'border-ttp-primary border-4': isLanguage == 'cn' }" class="md:p-4 rounded-xl overflow-hidden">
                    <img :src="languages.cn" />
                  </button>
                  <button @click="isLanguage = 'jp'" :class="{ 'border-ttp-primary border-4': isLanguage === 'jp', 'border-black border-2': isLanguage !== 'jp' }" class="md:p-4 rounded-xl overflow-hidden overflow-h">
                    <img :src="languages.jp" />
                  </button>
                  <button @click="isLanguage = 'kr'" :class="{ 'border-ttp-primary border-4': isLanguage === 'kr', 'border-black border-2': isLanguage !== 'kr' }" class="md:p-4 rounded-xl overflow-hidden">
                    <img :src="languages.kr" />
                  </button>
                  <button @click="isLanguage = 'ru'" :class="{ 'border-ttp-primary border-4': isLanguage === 'ru', 'border-black border-2': isLanguage !== 'ru' }" class="md:p-4 rounded-xl overflow-hidden">
                    <img :src="languages.ru" />
                  </button>
                </div>

                <div class="ttp-between mt-4">
                  <ButtonDefaultComponent @click="submitLanguage" :classTagName="'w-full'" :isSpecial="false" :isVisible="true" :isIcon="true" :isLoading="loading" :isDisable="loading" :htmlIcon="''" :isSubmit="true" :title="$t('btn.btn_select_default')" :classEnumName="$enums.ENUM_COLOR.DEFAULT_1" :size="$enums.ENUM_SIZE.SMALL" />
                </div>
              </div>
            </section>
          </div>
          <div class="text-right ttp-text-xs text-[#94a3b8]">v.{{ appVersion }}</div>
        </div>
      </div>
    </main>
  </div>
</template>
